import { ObjectConfig, required } from "@homebound/form-state";
import { ChangeEventWorkflow, SaveChangeEventMaterialVariantSwapInput } from "src/generated/graphqlTypes";
import {
  baseChangeEventFormConfig,
  BaseChangeEventFormInput,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { reviewStep } from "src/routes/projects/change-events/guided/workflows/sharedSteps/sharedSteps";
import { useReviewStepActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useReviewStepActions";
import { SelectProductToReplaceStep } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/1.SelectProductToReplaceStep";
import { ReviewImpactStep } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/2.ReviewImpactStep";
import { defineWorkflow } from "src/routes/projects/change-events/guided/workflows/types";
import { useMaybeOpenCommitmentCleanupModal } from "./useMaybeOpenCommitmentCleanupModal";
import { useSaveSwapProductChangeEvent } from "./useSaveSwapProductChangeEvent";

export type SwapProductFormSwap = SaveChangeEventMaterialVariantSwapInput;

export type SwapProductFormInput = BaseChangeEventFormInput & { swaps: SwapProductFormSwap[] };

// Exported so tests can build their form state from the same config the runner uses.
export const swapProductFormConfig: ObjectConfig<SwapProductFormInput> = {
  ...baseChangeEventFormConfig,
  swaps: {
    type: "list",
    // At least one swap is needed to create the change event. The list is exactly what gets sent,
    // so removing a swap removes its row and an empty list means there is nothing to save.
    rules: [({ value }) => (value.nonEmpty ? undefined : "At least one product swap is required")],
    config: {
      id: { type: "value" },
      proposedMaterialVariantId: { type: "value", rules: [required] },
      originalProjectItemId: { type: "value", rules: [required] },
      proposedMaterialVariantWasCreated: { type: "value" },
      totalCostInCents: { type: "value" },
      totalPriceInCents: { type: "value" },
    },
  },
};

export const swapProductWorkflow = defineWorkflow<SwapProductFormInput>({
  workflow: ChangeEventWorkflow.SwapProduct,
  formConfig: swapProductFormConfig,
  // In edit mode, map the saved swaps back to the form state. The fragment nests the original PI and
  // the proposed MV, so flatten them to the ids the form (and the save input) work in.
  mapToForm: (changeEvent) => ({
    id: changeEvent.id,
    workflow: changeEvent.workflow?.code,
    projectStageId: changeEvent.projectStage.id,
    type: changeEvent.type.code,
    title: changeEvent.title,
    internalNote: changeEvent.internalNote,
    reasonId: changeEvent.reason.id,
    hasPreSignedContract: changeEvent.hasPreSignedContract,
    documentId: changeEvent.document?.id,
    documentName: changeEvent.document?.name,
    swaps: changeEvent.materialVariantSwaps.map((swap) => ({
      id: swap.id,
      originalProjectItemId: swap.originalProjectItem.id,
      proposedMaterialVariantId: swap.proposedMaterialVariant.id,
      proposedMaterialVariantWasCreated: swap.proposedMaterialVariantWasCreated,
      // The server derives these two as `override ?? 0`, so a swap nobody has priced yet reads back as
      // 0. Blank it out so Review Impact shows its "Enter Cost"/"Enter Price" placeholder rather than
      // "$0.00".
      totalCostInCents: swap.totalCostInCents || undefined,
      totalPriceInCents: swap.totalPriceInCents || undefined,
    })),
  }),
  steps: [
    {
      value: GuidedChangeEventStep.SelectProductToReplace,
      label: "Select Product",
      isComplete: (form) => form.swaps.valid,
      render: (form) => <SelectProductToReplaceStep formState={form} />,
    },
    {
      value: GuidedChangeEventStep.ReviewImpact,
      label: "Review Impact",
      isComplete: (form) => form.swaps.valid,
      render: (form) => <ReviewImpactStep formState={form} />,
    },
    reviewStep(),
  ],
  initialStep: ({ form }) => {
    return form.swaps.valid ? GuidedChangeEventStep.ReviewImpact : GuidedChangeEventStep.SelectProductToReplace;
  },
  useActions: (args) => {
    const { saveChangeEvent } = useSaveSwapProductChangeEvent(args);
    const { submit } = useReviewStepActions(args);
    const saveAndExit = () => saveChangeEvent({ andExit: true });
    // Swapping a product rewrites scope that may already be on a purchase order, and the backend
    // won't touch a live PO, so submitting is gated on those being cleaned up by hand first.
    const { submitWithCommitmentCleanup } = useMaybeOpenCommitmentCleanupModal({
      form: args.form,
      onSubmit: submit,
      onSaveDraftAndExit: saveAndExit,
    });
    return {
      onSubmit: submitWithCommitmentCleanup,
      onContinue: {
        [GuidedChangeEventStep.SelectProductToReplace]: saveChangeEvent,
        [GuidedChangeEventStep.ReviewImpact]: saveChangeEvent,
      },
      onSaveAndExit: args.form.value.id ? saveAndExit : undefined,
    };
  },
});
